import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Header from 'src/components/header';
import NotFound from 'src/components/not-found';
import ScannedContainersList from 'src/components/ScannedContainersList';
import useMaterialLotNamesByContainers from 'src/hooks/useMaterialLotNamesByContainers';
import useQueryParams from 'src/hooks/useQueryParams';
import { api } from 'src/utils/api';
import { API_RESOURCES, MATERIAL_BATCH_ACTIONS, PERMANENT_CONTAINER_ACTIONS } from 'src/utils/constants';
import { formatList, pluralWord } from 'src/utils/stringUtils';
import { getResourceUri, getRouteURI, getShortUuid, getUuid } from 'src/utils/url';
import userPropType from 'src/utils/user-prop-type';

import routes from '../../../utils/routes';

const CreateInitialBatchPage = ({ user, error }) => {
  const { uuid: materialContainerUuid } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const printerUri = queryParams.get("printer");
  const {
    scannedContainers: _scannedContainers = '',
  } = useQueryParams();

  const scannedContainers = useMemo(
    () => (_scannedContainers ? _scannedContainers.split(',').map((uuid) => uuid.trim()).filter(Boolean) : []),
    [_scannedContainers],
  );

  const [isSubmitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const navigate = useNavigate();

  const {
    namesByUuid: materialLotNamesByContainer,
    isLoading: isMaterialLotsLoading,
  } = useMaterialLotNamesByContainers({
    containerUuids: scannedContainers,
  });

  const buildMixedMaterialLotError = () => {
    const groupedByLot = scannedContainers.reduce((acc, containerUuid) => {
      const lotName = materialLotNamesByContainer[containerUuid] ?? 'Unknown Material Lot';
      if (!acc[lotName]) {
        acc[lotName] = [];
      }
      acc[lotName].push(containerUuid);
      return acc;
    }, {});

    const lotSections = Object.entries(groupedByLot).map(([lotName, containerUuids]) => (
      <p key={lotName} className="mb-2">
        Container {formatList(containerUuids.map((uuid) => `[${getShortUuid(uuid)}]`))} come from Material Lot [{lotName}].
      </p>
    ));

    return (
      <>
        {lotSections}
        <p className="mb-0 mt-3">
          If you wish to blend these containers together, please create separate initial batches for each Material Lot before complete the blend.
        </p>
      </>
    );
  };

  const onInitialBatchCreate = async (containerUuids) => {
    try {
      // API expects list of objects with single URI key
      const scannedContainerUris = containerUuids.map((uuid) => {
        return { uri: getResourceUri(API_RESOURCES.MATERIAL_CONTAINER, uuid) };
      });

      const payload = {
        metadata: {
          containers: scannedContainerUris,
        },
        /* eslint-disable camelcase */
        source_batch: null,
        action_type: MATERIAL_BATCH_ACTIONS.CREATE_INITIAL_BATCH,
        /* eslint-enable camelcase */
      };

      setSubmitting(true);

      const batchActionResponse = await api.post(`${API_RESOURCES.MATERIAL_BATCH_ACTION}/`, {
        json: payload,
      }).json();
      const batchUri = batchActionResponse['source_batch'];

      setSubmitting(false);

      const printerLoadMaterialBatchActionQueryParams = {
        type: PERMANENT_CONTAINER_ACTIONS.LOAD_MATERIAL,
        batch: batchUri,
        action: PERMANENT_CONTAINER_ACTIONS.LOAD_MATERIAL,
        isLoad: true,
        initialBatchAction: true,
        actionFromPrinter: true,
        isFillWithEntireContainer: true,
      };

      const isValid = value => value && value !== 'null';

      if (isValid(printerUri)) {
        navigate(getRouteURI(routes.printerLoadMaterialBatchAction,
          { uuid: getUuid(printerUri) },
          printerLoadMaterialBatchActionQueryParams));
      } else {
        navigate(getRouteURI(routes.materialContainer,
          {},
          {
            batch: getUuid(batchUri),
          }));
      }
    } catch (error_) {
      const { errors } = await error_.response.json();
      const title = errors[0].title;
      if (title?.includes('Received container(s) from different material lots')) {
        setSubmitError(buildMixedMaterialLotError());
      } else {
        setSubmitError(title);
      }
      setSubmitting(false);
      return;
    }

    return new Promise(() => {
    });
  };

  if (error || submitError) {
    return (
      <>
        <Header title="Create New Batch" user={user} />
        {
          error ?
            <main role="main" className="text-center">
              <NotFound id={materialContainerUuid} />
            </main>
            :
            <div className="alert alert-danger m-4" role="alert">
              <h1 className="h5 header-margin">Error</h1>
              <div className="mb-0">{submitError}</div>
            </div>
        }
      </>
    );
  }

  const containerCount = scannedContainers.length;
  const cancelRoute = getRouteURI(
    routes.materialContainerDetails,
    { uuid: materialContainerUuid },
    { scannedContainers },
  );

  return (
    <>
      <Header title="Create New Batch" user={user} />
      <main role="main" className="create-batch-page">
        <div className="materials-narrow-column create-batch-page__content">
          <h2 className="container-selection-title header-margin fs-mobile-20">
            Create new batch with selected amount
          </h2>

          <section className="create-batch-summary" aria-label="Selected containers">
            <div className="create-batch-summary__header">
              <span className="badge-circle create-batch-summary__count" aria-hidden="true">
                {containerCount}
              </span>
              <div className="create-batch-summary__label">
                <h2 className="create-batch-summary__title fs-mobile-18">
                  {containerCount} {pluralWord('container', containerCount)} selected
                </h2>
                <p className="create-batch-summary__hint">
                  Review the list below before confirming.
                </p>
              </div>
            </div>

            <ScannedContainersList
              containerUuids={scannedContainers}
              namesByUuid={materialLotNamesByContainer}
              isLoading={isMaterialLotsLoading}
              ariaLabel="Selected containers"
            />
          </section>

          <div className="create-batch-page__actions">
            <button
              type="button"
              className="btn btn-lg btn-primary btn-block fs-mobile-18"
              disabled={isSubmitting || containerCount === 0}
              onClick={() => onInitialBatchCreate(scannedContainers)}
            >
              {isSubmitting ? 'Creating…' : 'Confirm'}
            </button>
            <Link to={cancelRoute} className="materials-action-link">
              <button
                type="button"
                className="btn btn-default btn-action btn-block fs-mobile-18"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </Link>
          </div>
        </div>
      </main>
    </>
  );
};

CreateInitialBatchPage.propTypes = {
  error: PropTypes.shape({}),
  user: userPropType,
};

CreateInitialBatchPage.defaultProps = {
  error: null,
  user: null,
};

export default CreateInitialBatchPage;
